import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPublicSettings, API } from "@/lib/api";

/**
 * Applies the admin-configured favicon to the browser tab and swaps the PWA
 * manifest to a dynamic one so installed apps also pick up the custom icon.
 * Mounted once at App root — has no visual output.
 */
export default function FaviconManifestInjector() {
    const { data } = useQuery({
        queryKey: ["public-settings"],
        queryFn: fetchPublicSettings,
        staleTime: 60_000,
    });

    useEffect(() => {
        const favicon = (data?.favicon_url || "").trim();
        const head = document.head;
        if (!head) return;

        // ---- Browser tab icons ----
        const ensureLink = (rel, sizes) => {
            const selector = sizes
                ? `link[rel="${rel}"][sizes="${sizes}"]`
                : `link[rel="${rel}"]`;
            let el = head.querySelector(selector);
            if (!el) {
                el = document.createElement("link");
                el.setAttribute("rel", rel);
                if (sizes) el.setAttribute("sizes", sizes);
                head.appendChild(el);
            }
            return el;
        };

        if (favicon) {
            const iconLink = ensureLink("icon", "32x32");
            iconLink.setAttribute("type", favicon.startsWith("data:image/svg") ? "image/svg+xml" : "image/png");
            iconLink.setAttribute("href", favicon);

            const shortcutLink = ensureLink("shortcut icon");
            shortcutLink.setAttribute("href", favicon);

            const appleLink = ensureLink("apple-touch-icon");
            appleLink.setAttribute("href", favicon);
        }

        // ---- PWA manifest ----
        // Always point to the backend's dynamic manifest so installed apps get
        // the current site name + favicon, even when the DB changes later.
        let manifestLink = head.querySelector('link[rel="manifest"]');
        if (!manifestLink) {
            manifestLink = document.createElement("link");
            manifestLink.setAttribute("rel", "manifest");
            head.appendChild(manifestLink);
        }
        manifestLink.setAttribute("href", `${API}/manifest.json`);

        // ---- Document title respects custom site name ----
        const siteName = (data?.site_name || "").trim();
        if (siteName && !document.title.startsWith(siteName)) {
            document.title = `${siteName} · Live Indian & Global Stock Market News`;
        }
    }, [data?.favicon_url, data?.site_name]);

    return null;
}
